import Bugsnag from "@bugsnag/expo";
import { loadAsync } from "expo-font";
import { useEffect, useState } from "react";
import { Platform, useWindowDimensions } from "react-native";
import { APP_ENV } from "./src/client";
import App from "./App";
import FakeSplashScreen from "./src/components/FakeSplashScreen/FakeSplashScreen";
import * as SplashScreen from "expo-splash-screen";
import { checkForUpdateAndApply } from "./src/hooks/useUpdateOnFocus";
import { cmsDataAtom, userAtom } from "./src/atoms";
import { useSetAtom } from "jotai/react";

import { getCmsData } from "./src/client/cms";
import useRefreshUser from "./src/hooks/useRefreshUser";
import {
  emitEvent,
  ensureOrientationLockPatched,
} from "./src/components/orientationLockBus";
import * as ScreenOrientation from "expo-screen-orientation";
import { fetchOrInitializeAnonUserFromStorage } from "./src/client/account";

// Prevent native splash screen from autohiding before App component declaration
if (Platform.OS != "web") {
  SplashScreen.preventAutoHideAsync()
    .then((result) => {
      console.log(`SplashScreen.preventAutoHideAsync() succeeded: ${result}`);

      Bugsnag.leaveBreadcrumb("SplashScreen.preventAutoHideAsync() succeeded");
    })
    .catch(console.warn);
}

ensureOrientationLockPatched();

// might need to put this in a shared file as this file
// is only for web really
let skipBugsnag = false;
if (Platform.OS === "web") {
  // initialize dataLayer
  window.dataLayer = window?.dataLayer || [];
  if (window.location.href.includes("localhost")) {
    skipBugsnag = true;
  }
}

if (!skipBugsnag && !Bugsnag.isStarted()) {
  Bugsnag.start({
    releaseStage: APP_ENV,
  });
}

const loadFontsAsync = async () => {
  try {
    const fontsModule = await import("./src/styles/fonts");
    await loadAsync(fontsModule.FONTS);
  } catch (err) {
    console.error("Error loading fonts.... ", err);
    Bugsnag.notify(
      new Error(`[Setup] Error loading fonts during startup: ${err}`)
    );
  }
};

const RootElement = () => {
  const [ready, setReady] = useState(false);
  const setCmsData = useSetAtom(cmsDataAtom);
  const { refreshUser } = useRefreshUser();
  const setUser = useSetAtom(userAtom);
  const TABLET_MIN_DP = 600;
  const { width, height } = useWindowDimensions();
  const shortest = Math.min(width, height);
  const isTabletAndUp = shortest >= TABLET_MIN_DP;

  useEffect(() => {
    const init = async () => {
      // If the app is not ready, we can show a splash screen
      if (!ready) {
        console.log("[Setup] Initializing App...");
        await SplashScreen.hideAsync();

        Bugsnag.leaveBreadcrumb("App initialization started");

        // 1. Check for OTA update and apply if needed
        const updatePerformed = await checkForUpdateAndApply();
        if (updatePerformed) {
          // The app will reload if an update is applied,
          // so return early in that case.
          return;
        }

        try {
          if (isTabletAndUp) {
            await ScreenOrientation.unlockAsync();
            emitEvent({ note: "unlocked screen orientation" });
          } else {
            await ScreenOrientation.lockAsync(
              ScreenOrientation.OrientationLock.PORTRAIT_UP
            );
            emitEvent({ note: "locked to PORTRAIT_UP" });
          }
        } catch (error) {
          console.error("Error locking screen orientation:", error);
          emitEvent({ note: "error locking screen orientation" });
        }

        // Hide the native splash screen so the FakeSplashScreen can take over
        Bugsnag.leaveBreadcrumb("[Setup] Native Splash Screen hidden");
        console.log("[Setup] Native Splash Screen hidden");

        // init bugsnag nomatter what
        if (!skipBugsnag && !Bugsnag.isStarted()) {
          Bugsnag.start({
            releaseStage: APP_ENV,
          });
        }

        // sleep for 500ms to avoid black screen during swappout
        await new Promise((resolve) => setTimeout(resolve, 500));

        await loadFontsAsync()
          .then(async () => {
            console.log("[Setup] Fonts Loaded");
            Bugsnag.leaveBreadcrumb("[Setup] Fonts Loaded");

            await getCmsData()
              .then((cmsData) => {
                setCmsData(cmsData);
                console.log("[Setup] Fetched Settings");
              })
              .catch((err) => {
                console.error("[Setup] Error fetching CMS data: ", err);
                Bugsnag.notify(
                  new Error(`[Setup] Error fetching CMS data: ${err}`)
                );
              });

            // refresh user data
            await refreshUser()
              .then((r) => {
                if (r === "no_user") {
                  fetchOrInitializeAnonUserFromStorage().then((anon) => {
                    setUser(anon);
                  });
                }
                console.log("[Setup] User data refreshed");
                Bugsnag.leaveBreadcrumb("[Setup] User data refreshed");
              })
              .catch((err) => {
                console.error("[Setup] Error refreshing user data: ", err);
                Bugsnag.notify(
                  new Error(`[Setup] Error refreshing user data: ${err}`)
                );
              });

            // Other stuff..
            console.log("[Setup] App Initialized");
            Bugsnag.leaveBreadcrumb("[Setup] Fonts Loaded");
            setReady(true);
          })
          .catch((err) => {
            console.error("[Setup] Error during app startup: ", err);
            Bugsnag.notify(
              new Error(`[Setup] Error during app startup: ${err}`)
            );
            setReady(true); // set ready to true even if fonts fail to load as its better than a blank screen
          });
      }
    };

    init();
  }, []);

  if (!ready) {
    return <FakeSplashScreen />;
  }

  return <App ready={ready} />;
};

export default RootElement;
